import {
  PostTypeCarousel,
  PostTypeEvent,
  PostTypeFacebookReel,
  PostTypeFacebookStory,
  PostTypeGhostPost,
  PostTypeOffer,
  PostTypePost,
  PostTypeReel,
  PostTypeShort,
  PostTypeStory,
  PostTypeThread,
  PostTypeWhatsNew,
} from '~publish/legacy/post/constants'

import { AttachmentTypes, MediaAttachmentTypes } from '../attachments'
import { createService } from './createService'

export enum Service {
  Instagram = 'instagram',
  Facebook = 'facebook',
  Twitter = 'twitter',
  Linkedin = 'linkedin',
  Pinterest = 'pinterest',
  Tiktok = 'tiktok',
  Googlebusiness = 'googlebusiness',
  StartPage = 'startPage',
  Mastodon = 'mastodon',
  Youtube = 'youtube',
  Threads = 'threads',
  Bluesky = 'bluesky',
  Substack = 'substack',
}

export const ServiceOmni = createService('omni', {
  isOmni: true,
  maxAttachableImagesCount: () => 20,
  canEditLinkAttachment: true,
  unavailableMediaAttachmentTypes: [],
})

export const ServiceTwitter = createService(Service.Twitter, {
  charLimit: (draft) => {
    return draft?.isTwitterPremium ? 25000 : 280
  },
  formattedName: 'Twitter / X',
  unavailableAttachmentTypes: [AttachmentTypes.LINK],
  maxAttachableImagesCount: (): number => 4,
  // @see domains/updates-domain/src/use-cases/create/validators/services/twitter/rules.ts (maxAssetsRule(4))
  maxAttachableAssetsCount: (): number => 4,
  altTextCharLimit: 1000,
  videoMaxSize: 512 * 1024 * 1024, // 512MB
  videoMaxDurationMs: 140 * 1000, // 140s
  shouldShowDuplicateContentWarning: true,
  unavailableMediaAttachmentTypes: [
    MediaAttachmentTypes.WEBM,
    MediaAttachmentTypes.PDF,
  ],
  transcodeVideo: true,
  imageMaxSize: 5 * 1024 * 1024, // 5MB
  gifMaxSize: 15 * 1024 * 1024, // 15MB,
  supportsThread: () => true,
  maxThreads: 25,
  nameOfPost: 'Tweet',
  supportsChannelFields: true,
})

export const ServiceThreads = createService(Service.Threads, {
  charLimit: 500,
  allowedPostTypes: [PostTypeThread, PostTypeGhostPost],
  altTextCharLimit: 1000,
  formattedName: 'Threads',
  maxAttachableImagesCount: (): number => 20,
  // @see domains/updates-domain/src/use-cases/create/validators/services/threads/rules.ts (maxAssetsRule(20))
  maxAttachableAssetsCount: (): number => 20,
  unavailableAttachmentTypes: {
    [PostTypeThread]: [AttachmentTypes.RETWEET],
    [PostTypeGhostPost]: [
      AttachmentTypes.RETWEET,
      AttachmentTypes.LINK,
      AttachmentTypes.MEDIA,
      AttachmentTypes.REELS,
    ],
  },
  videoMaxSize: 1024 * 1024 * 1024, // 1GB
  videoMaxDurationMs: 300 * 1000, // 300s
  videoMinFrameRate: 23,
  videoMaxFrameRate: 60,
  videoMaxWidth: 1920,
  shouldShowDuplicateContentWarning: true,
  unavailableMediaAttachmentTypes: [
    MediaAttachmentTypes.WEBM,
    MediaAttachmentTypes.PDF,
    MediaAttachmentTypes.GIF,
  ],
  transcodeVideo: true,
  imageMaxSize: 8 * 1024 * 1024, // 8MB
  supportsThread: (updateType = ServiceThreads.getDefaultPostType()) => {
    if (updateType === PostTypeGhostPost) {
      return false
    }

    return true
  },
  maxThreads: 25,
  maxLinks: 5,
  canEditLinkAttachment: false,
  supportsChannelFields: true,
})

export const ServiceMastodon = createService(Service.Mastodon, {
  charLimit: (draft) => draft?.mastodonMaxCharacters ?? 500,
  altTextCharLimit: 1500,
  unavailableAttachmentTypes: [AttachmentTypes.LINK, AttachmentTypes.RETWEET],
  unavailableMediaAttachmentTypes: [MediaAttachmentTypes.PDF],
  maxAttachableImagesCount: () => 4,
  videoMaxSize: 99 * 1024 * 1024, // 99MB
  transcodeVideo: false,
  imageMaxSize: 16 * 1024 * 1024, // 16MB
  imageMaxWidth: 10000,
  imageMaxHeight: 10000,
  gifMaxSize: 16 * 1024 * 1024, // 16MB
  supportsThread: () => true,
  maxThreads: 25,
  isShorteningLinksAvailable: false,
  supportsChannelFields: true,
})

export const ServiceFacebook = createService(Service.Facebook, {
  charLimit: 5000,
  allowedPostTypes: [PostTypePost, PostTypeFacebookReel, PostTypeFacebookStory],
  altTextCharLimit: 1000,
  commentCharLimit: 5000,
  unavailableAttachmentTypes: [AttachmentTypes.RETWEET],
  unavailableMediaAttachmentTypes: [MediaAttachmentTypes.PDF],
  maxAttachableImagesCount: (draft) => {
    if (!draft) {
      return 10
    }

    if (draft.isFacebookStoryPost()) {
      return 1
    }

    return 10
  },
  canEditLinkAttachment: true,
  canEditVideoTitle: true,

  videoMinResolution: 540,
  videoMaxSize: 1024 * 1024 * 1024, // 1GB

  // REELS
  videoMinDurationMsReels: 3 * 1000, // 3 seconds for Reels
  videoMaxDurationMsReels: 90 * 1000, // 90 seconds for Reels
  videoMaxSizeReels: 1024 * 1024 * 1024, // 1 GB for Reels
  videoMinFrameRateReels: 24, // frames per second
  videoMaxFrameRateReels: 60, // frames per second
  videoMinWidthReels: 540,
  videoMinHeightReels: 960,
  videoMinAspectRatioReels: 9 / 16,
  videoMaxAspectRatioReels: 16 / 9,
  // STORIES
  videoMinDurationMsStories: 3 * 1000, // 3 seconds for Stories
  videoMaxDurationMsStories: 60 * 1000, // 60 seconds for Stories
  videoMaxSizeStories: 100 * 1024 * 1024, // 100MB
  videoMinWidthStories: 540,
  videoMinHeightStories: 960,
  videoMinAspectRatioStories: 6 / 16,
  videoMaxAspectRatioStories: 9 / 16,
  imageMaxSizeStories: 4 * 1024 * 1024, // 4MB for Stories
  videoMinFrameRateStories: 24, // frames per second
  videoMaxFrameRateStories: 60, // frames per second
  supportsMobileReminders: true,
  supportsChannelFields: true,
})

export const ServiceInstagram = createService(Service.Instagram, {
  allowedPostTypes: [PostTypePost, PostTypeReel, PostTypeStory],
  charLimit: 2200,
  commentCharLimit: 2200,
  formattedName: 'Instagram',
  altTextCharLimit: 1000,
  imageMaxSize: 8 * 1024 * 1024, // 8MB
  imageMaxWidth: 5000, // Instagram auto-downscales images wider than 1440px
  unavailableAttachmentTypes: [AttachmentTypes.LINK, AttachmentTypes.RETWEET],
  maxAttachableImagesCount: (draft) => {
    if (!draft) {
      return 10
    }

    if (draft.isStoryPost()) {
      return 10
    }

    if (draft.isReelsPost()) {
      return 1
    }

    return 10
  },
  // @see domains/updates-domain/src/use-cases/create/validators/services/instagram/rules.ts
  // (maxAssetsRule(1) for Reels, maxAssetsRule(10) otherwise)
  maxAttachableAssetsCount: (draft) => (draft?.isReelsPost() ? 1 : 10),
  requiredAttachmentType: AttachmentTypes.MEDIA,
  videoMinDurationMs: 3 * 1000, // 3s
  videoMaxDurationMs: 60 * 1000, // 60s
  videoMaxSize: 1024 * 1024 * 1024, // 1 GB (gets transformed to Reels)
  videoMinFrameRate: 23,
  videoMaxFrameRate: 60,
  usesImageFirstLayout: true,
  maxHashtags: 5,
  maxCommentHashtags: 30,
  maxMentions: 20,
  maxUserTags: 20,
  canHaveLocation: true,
  canHaveUserTags: true,
  canEditVideoAttachment: true,
  canEditVideoTitle: true,
  unavailableMediaAttachmentTypes: [
    MediaAttachmentTypes.WEBM,
    MediaAttachmentTypes.PDF,
    MediaAttachmentTypes.GIF,
  ],
  // REELS
  videoMinDurationMsReels: 3 * 1000, // 3 seconds for Reels
  videoMaxDurationMsReels: 15 * 60 * 1000, // 15 minutes for Reels
  videoMaxSizeReels: 1024 * 1024 * 1024, // 1 GB for Reels
  // STORIES
  videoMinDurationMsStories: 3 * 1000, // 3 seconds for Stories
  videoMaxDurationMsStories: 60 * 1000, // 60 seconds for Stories
  videoMaxSizeStories: 100 * 1024 * 1024, // 100 MB for Stories
  imageMaxSizeStories: 8 * 1024 * 1024, // 8MB for Stories
  isShorteningLinksAvailable: false,
  supportsMobileReminders: true,
  supportsChannelFields: true,
})

export const ServiceLinkedIn = createService(Service.Linkedin, {
  allowedPostTypes: [PostTypePost, PostTypeCarousel],
  charLimit: 3000,
  altTextCharLimit: 4000,
  formattedName: 'LinkedIn',
  videoMaxSize: 1024 * 1024 * 1024, // 1GB
  unavailableAttachmentTypes: [AttachmentTypes.RETWEET],
  canEditLinkAttachment: true,
  canEditVideoAttachment: false,
  maxAttachableImagesCount: () => 20,
  transcodeVideo: false,
  commentCharLimit: 1250,
  gifMaxSize: 10 * 1024 * 1024, // 10MB,
  gifMaxFrames: 250,
  supportsChannelFields: true,
})

export const ServiceGoogleBusiness = createService(Service.Googlebusiness, {
  allowedPostTypes: [PostTypeWhatsNew, PostTypeOffer, PostTypeEvent],
  charLimit: 1500,
  formattedName: 'Google Business Profile',
  unavailableAttachmentTypes: [AttachmentTypes.RETWEET, AttachmentTypes.LINK],
  usesImageFirstLayout: true,
  videoMaxSize: 100 * 1024 * 1024, // 100MB
  imageMaxSize: 25 * 1024 * 1024, // 25MB
  imageMinWidth: 250,
  imageMinHeight: 250,
  maxAttachableImagesCount: () => 1,
  unavailableMediaAttachmentTypes: [
    MediaAttachmentTypes.WEBM,
    MediaAttachmentTypes.M4V,
    MediaAttachmentTypes.GIF,
    MediaAttachmentTypes.MP4,
    MediaAttachmentTypes.VIDEO,
    MediaAttachmentTypes.MOV,
    MediaAttachmentTypes.AVI,
    MediaAttachmentTypes.PDF,
    // Google rejects a webp source URL with an opaque "Internal error
    // encountered", so the post fails at broadcast with no usable reason.
    MediaAttachmentTypes.WEBP,
  ],
  attachmentMinSize: 10 * 1024, // 10KB
  supportsChannelFields: true,
})

export const ServicePinterest = createService(Service.Pinterest, {
  unavailableAttachmentTypes: [AttachmentTypes.LINK, AttachmentTypes.RETWEET],
  requiredAttachmentType: AttachmentTypes.MEDIA,
  videoMinDurationMs: 4 * 1000, // 3s
  videoMaxDurationMs: 15 * 60 * 1000, // 15 min
  videoMaxSize: 1024 * 1024 * 1024, // 1GB
  hasSubprofiles: true,
  titleCharLimit: 100,
  charLimit: 500,
  altTextCharLimit: 500,
  canHaveSourceUrl: true,
  requiresText: false,
  requiresSourceUrl: true,
  usesImageFirstLayout: true,
  canEditVideoAttachment: true,
  canEditVideoTitle: true,
  unavailableMediaAttachmentTypes: [
    MediaAttachmentTypes.WEBM,
    MediaAttachmentTypes.PDF,
  ],
  transcodeVideo: false,
  isShorteningLinksAvailable: false,
  supportsChannelFields: true,
})

export const ServiceTiktok = createService(Service.Tiktok, {
  unavailableAttachmentTypes: [AttachmentTypes.LINK, AttachmentTypes.RETWEET],
  unavailableMediaAttachmentTypes: [
    MediaAttachmentTypes.GIF,
    MediaAttachmentTypes.AVI,
    MediaAttachmentTypes.PDF,
  ],
  requiredAttachmentType: AttachmentTypes.MEDIA,
  requiresText: false,
  usesImageFirstLayout: true,
  canEditVideoAttachment: true,
  canEditVideoTitle: false,
  maxAttachableImagesCount: () => 10,
  videoMaxSize: 1_000_000_000, // ~954MB
  videoMinResolution: 360,
  charLimit: (draft) => (draft?.video ? 2200 : 4000),
  videoMinDurationMs: 3 * 1000, // 3s
  // 10 minutes and 0.9 seconds because a bit more should be allowed
  // to accommodate for videos slightly longer.
  videoMaxDurationMs: 600_900,
  imageMaxSize: 25 * 1024 * 1024, // 25MB
  imageMaxPixelCount: 2_073_600, // 1920x1080 or 1080x1920
  titleCharLimit: 90,
  transcodeVideo: false,
  supportsMobileReminders: true,
  supportsChannelFields: true,
})

export const ServiceYoutube = createService(Service.Youtube, {
  allowedPostTypes: [PostTypeShort],
  unavailableAttachmentTypes: [AttachmentTypes.LINK, AttachmentTypes.RETWEET],
  unavailableMediaAttachmentTypes: [
    MediaAttachmentTypes.JPG,
    MediaAttachmentTypes.JPEG,
    MediaAttachmentTypes.PNG,
    MediaAttachmentTypes.GIF,
    MediaAttachmentTypes.IMAGE,
    MediaAttachmentTypes.PDF,
  ],
  requiredAttachmentType: AttachmentTypes.MEDIA,
  requiresText: false,
  usesImageFirstLayout: false,
  canEditVideoAttachment: false,
  maxAttachableImagesCount: () => 1,
  videoMaxSize: 10 * 1024 * 1024 * 1024, // 10GB
  videoMinResolution: 360,
  charLimit: 5000,
  titleCharLimit: 100,
  videoMinDurationMs: 1 * 1000, // 1s
  videoMaxDurationMs: 180 * 1000, // 180s
  transcodeVideo: false,
  onlyAllowsVerticalVideo: true,
  supportsMobileReminders: true,
  supportsChannelFields: true,
})

export const ServiceStartPage = createService(Service.StartPage, {
  charLimit: 5000,
  altTextCharLimit: 1000,
  formattedName: 'Start Page',
  unavailableAttachmentTypes: [AttachmentTypes.RETWEET, AttachmentTypes.LINK],
  usesImageFirstLayout: true,
  imageMaxSize: 25 * 1024 * 1024, // 25MB
  maxAttachableImagesCount: () => 1,
  unavailableMediaAttachmentTypes: [
    MediaAttachmentTypes.WEBM,
    MediaAttachmentTypes.M4V,
    MediaAttachmentTypes.MP4,
    MediaAttachmentTypes.VIDEO,
    MediaAttachmentTypes.MOV,
    MediaAttachmentTypes.AVI,
    MediaAttachmentTypes.PDF,
  ],
  attachmentMinSize: 10 * 1024, // 10KB
  supportsChannelFields: true,
})

export const ServiceBluesky = createService(Service.Bluesky, {
  charLimit: 300,
  altTextCharLimit: 2000,
  formattedName: 'Bluesky',
  unavailableAttachmentTypes: [AttachmentTypes.RETWEET],
  canEditLinkAttachment: true,
  shouldShowDuplicateContentWarning: true,
  unavailableMediaAttachmentTypes: [
    MediaAttachmentTypes.M4V,
    MediaAttachmentTypes.AVI,
    MediaAttachmentTypes.PDF,
  ],
  maxAttachableImagesCount: () => 10,
  imageMaxSize: 8 * 1024 * 1024, // 8MB
  gifMaxSize: 15 * 1024 * 1024, // 15MB
  videoMaxSize: 300_000_000, // ~286MB
  videoMaxDurationMs: 10 * 60 * 1000, // 10min
  supportsThread: () => true,
  maxThreads: 25,
})

export const ServiceSubstack = createService(Service.Substack, {
  charLimit: 10_000,
  formattedName: 'Substack',
  nameOfPost: 'Note',
  unavailableAttachmentTypes: [AttachmentTypes.RETWEET],
  // Substack fetches the link and builds its own card; Buffer sends the URL only.
  canEditLinkAttachment: false,
  shouldShowDuplicateContentWarning: true,
  // Substack documents `video/*` without naming the containers it accepts, so
  // WEBM stays out until a real upload confirms it, as it does for every other
  // network here. Documents are refused outright.
  unavailableMediaAttachmentTypes: [
    MediaAttachmentTypes.WEBM,
    MediaAttachmentTypes.PDF,
  ],
  maxAttachableImagesCount: () => 6,
  // @see domains/updates-domain/src/use-cases/create/validators/services/substack/rules.ts (maxAssetsRule(6))
  maxAttachableAssetsCount: () => 6,
  imageMaxSize: 8 * 1024 * 1024, // 8MB
  gifMaxSize: 15 * 1024 * 1024, // 15MB
  // Substack documents 20GB, but Buffer's uploader refuses a video over 1GB
  // long before a Note sees it.
  videoMaxSize: 1024 * 1024 * 1024, // 1GB
  videoMaxDurationMs: 15 * 60 * 1000, // 15 minutes
})
